import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

export const POLL_MS = 30000;

export function useFetch(url, params, { poll = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const key = JSON.stringify(params || {});
  const seq = useRef(0);
  const polling = useRef(false);

  const fetchData = useCallback(async (silent) => {
    if (!url) return;
    if (silent && polling.current) return;
    if (silent) polling.current = true;
    const mine = ++seq.current;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(url, { params: JSON.parse(key) });
      if (mine !== seq.current) return;
      setData(res.data);
      setUpdatedAt(new Date());
      setError("");
    } catch (e) {
      if (!silent && mine === seq.current) setError(formatApiError(e));
    } finally {
      if (silent) polling.current = false;
      if (!silent && mine === seq.current) setLoading(false);
    }
  }, [url, key]);

  const reload = useCallback(() => fetchData(false), [fetchData]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    if (!poll || !url) return undefined;
    const tick = () => { if (document.visibilityState === "visible") fetchData(true); };
    const id = setInterval(tick, POLL_MS);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); };
  }, [poll, url, fetchData]);

  return { data, loading, error, reload, setData, updatedAt };
}
